import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface UsageTrajProps {
  rows: RawIndicatorRow[];
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const toStr = (v: string | number | undefined): string => String(v ?? "").trim();

const toNum = (v: string | number | undefined): number => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const fmtInt = (n: number): string => Math.round(n).toLocaleString();

const fmtPct = (n: number): string => {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const extractScore = (text: string): number | undefined => {
  const m = /Score:\s*(\d+)/.exec(text);
  return m ? parseInt(m[1], 10) : undefined;
};

const formatISODate = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const y = d.getUTCFullYear();
    const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${mo}-${day}`;
  } catch {
    return iso;
  }
};

const getDateAge = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const diffDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 120 ? ">4mo" : "≤4mo";
  } catch {
    return "";
  }
};

// ─── data types ───────────────────────────────────────────────────────────────

interface UsageChangeData {
  currentMAU: number;
  avgMAU: number;
  mauChange: number;
  binText: string;
  scoringBins: string;
  score: number;
}

interface PlatformCountData {
  android: number;
  ios: number;
  mac: number;
  windows: number;
  current: number;
  growth: number;
  ruleText: string;
  scoringRules: string;
  score: number;
}

interface MgmtScopeData {
  mdmChange: number;
  mdmScore: number;
  mamChange: number;
  mamScore: number;
  suitePurchase: string;
  suiteScore: number;
  compositeEq: string;
  compositeTotal: number;
  scoringBins: string;
  score: number;
}

// ─── parsers ──────────────────────────────────────────────────────────────────

function parseUsageChange(rows: RawIndicatorRow[]): UsageChangeData {
  let currentMAU = 0, avgMAU = 0, mauChange = 0, binText = "", scoringBins = "", score = 0;
  for (const row of rows) {
    const lbl = toStr(row.factorLabel);
    const val = toStr(row.value);
    if (lbl === "Current MAU") currentMAU = toNum(row.value);
    else if (lbl === "4-month Average") avgMAU = toNum(row.value);
    else if (lbl === "MAU Change") mauChange = toNum(row.value);
    else if (lbl === "Bin:") { binText = val; score = extractScore(val) ?? score; }
    else if (lbl === "Scoring Bins:") scoringBins = val;
  }
  return { currentMAU, avgMAU, mauChange, binText, scoringBins, score };
}

function parsePlatformCount(rows: RawIndicatorRow[]): PlatformCountData {
  let android = 0, ios = 0, mac = 0, windows = 0, current = 0, growth = 0;
  let ruleText = "", scoringRules = "", score = 0;
  for (const row of rows) {
    const lbl = toStr(row.factorLabel);
    const val = toStr(row.value);
    if (lbl === "Android") android = toNum(row.value);
    else if (lbl === "iOS") ios = toNum(row.value);
    else if (lbl === "Mac") mac = toNum(row.value);
    else if (lbl === "Windows") windows = toNum(row.value);
    else if (lbl === "Current") current = toNum(row.value);
    else if (lbl === "Growth (vs 4-month Average)") growth = toNum(row.value);
    else if (lbl === "Rule:") { ruleText = val; score = extractScore(val) ?? score; }
    else if (lbl === "Scoring Bins:") scoringRules = val;
  }
  return { android, ios, mac, windows, current, growth, ruleText, scoringRules, score };
}

function parseMgmtScope(rows: RawIndicatorRow[]): MgmtScopeData {
  let mdmChange = 0, mdmScore = 0, mamChange = 0, mamScore = 0;
  let suitePurchase = "", suiteScore = 0, compositeEq = "", compositeTotal = 0, scoringBins = "";
  for (const row of rows) {
    const lbl = toStr(row.factorLabel);
    const val = toStr(row.value);
    if (lbl === "MDM Change (vs 4-month Average)") mdmChange = toNum(row.value);
    else if (lbl === "MDM Change (vs 4-month Average) Score") mdmScore = toNum(row.value);
    else if (lbl === "MAM Change (vs 4-month Average)") mamChange = toNum(row.value);
    else if (lbl === "MAM Change (vs 4-month Average) Score") mamScore = toNum(row.value);
    else if (lbl === "Suite Purchase") suitePurchase = val;
    else if (lbl === "Suite Purchase Score") suiteScore = toNum(row.value);
    else if (lbl === "Composite") compositeEq = val;
    else if (lbl === "Composite Total") compositeTotal = toNum(row.value);
    else if (lbl === "Scoring Bins:") scoringBins = val;
  }
  return { mdmChange, mdmScore, mamChange, mamScore, suitePurchase, suiteScore, compositeEq, compositeTotal, scoringBins, score: compositeTotal };
}

// ─── themes ───────────────────────────────────────────────────────────────────

const THEMES = {
  usage:    { accent: "#1252A3", bg: "#EBF3FB", border: "#BDD7EE" },
  platform: { accent: "#107C10", bg: "#EAF4EA", border: "#9ED89E" },
  mgmt:     { accent: "#5C2D91", bg: "#F3EEF9", border: "#C5A6E8" },
} as const;

// ─── shared sub-components ────────────────────────────────────────────────────

interface MetricHeaderProps {
  score: number;
  weight: number;
  contribution: number;
  accent: string;
}

const MetricHeader: React.FC<MetricHeaderProps> = ({ score, weight, contribution, accent }) => (
  <Stack tokens={{ childrenGap: 8 }}>
    <Stack horizontal styles={{ root: { justifyContent: "space-between" } }}>
      <Stack tokens={{ childrenGap: 2 }} horizontalAlign="center" styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C", textAlign: "center" } }}>Score</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: accent, textAlign: "center" } }}>{score}</Text>
      </Stack>
      <Stack tokens={{ childrenGap: 2 }} horizontalAlign="center" styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C", textAlign: "center" } }}>Weight</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: "#323130", textAlign: "center" } }}>{weight.toFixed(2)}%</Text>
      </Stack>
      <Stack tokens={{ childrenGap: 2 }} horizontalAlign="center" styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C", textAlign: "center" } }}>Contribution</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: "#323130", textAlign: "center" } }}>{contribution.toFixed(2)} pts</Text>
      </Stack>
    </Stack>
    <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />
  </Stack>
);

interface FactorBoxProps {
  label: string;
  value: string;
  valueColor?: string;
  minWidth?: number;
  flex?: string;
}

const FactorBox: React.FC<FactorBoxProps> = ({ label, value, valueColor = "#323130", minWidth = 90, flex }) => (
  <Stack
    horizontalAlign="center"
    tokens={{ childrenGap: 2 }}
    styles={{
      root: {
        backgroundColor: "#FFFFFF",
        border: "1px solid #EDEBE9",
        borderRadius: 8,
        padding: "8px 12px",
        minWidth,
        textAlign: "center",
        flex: flex ?? "0 0 auto",
      },
    }}
  >
    <Text variant="small" styles={{ root: { color: "#605E5C" } }}>{label}</Text>
    <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: valueColor } }}>{value}</Text>
  </Stack>
);

// ─── Usage Change Score Card ──────────────────────────────────────────────────

const UsageChangeCard: React.FC<{ data: UsageChangeData; weight: number }> = ({ data, weight }) => {
  const t = THEMES.usage;
  const contribution = (data.score * weight) / 100;
  const changeColor = data.mauChange > 0 ? "#107C10" : data.mauChange < 0 ? "#D13438" : "#323130";

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{ root: { backgroundColor: t.bg, border: `1.5px solid ${t.border}`, borderRadius: 12, padding: "20px 24px", flex: "1 1 0", minWidth: 280 } }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: t.accent } }}>Usage Change Score</Text>
      <MetricHeader score={data.score} weight={weight} contribution={contribution} accent={t.accent} />

      <Stack tokens={{ childrenGap: 8 }}>
        <Text variant="smallPlus" styles={{ root: { fontWeight: 600, color: t.accent } }}>Factors</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { flexWrap: "wrap" } }}>
          <FactorBox label="Current MAU" value={fmtInt(data.currentMAU)} />
          <FactorBox label="4-mo Avg" value={fmtInt(data.avgMAU)} />
          <FactorBox label="MAU Change" value={fmtPct(data.mauChange)} valueColor={changeColor} />
        </Stack>
      </Stack>

      {data.binText && (
        <Text variant="small" styles={{ root: { color: "#605E5C", fontStyle: "italic", textAlign: "center" } }}>
          Bin: {data.binText}
        </Text>
      )}

      {data.scoringBins && (
        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="small" styles={{ root: { fontWeight: 700, color: t.accent } }}>Scoring Bins:</Text>
          <Text variant="small" styles={{ root: { color: "#323130", lineHeight: "18px" } }}>{data.scoringBins}</Text>
        </Stack>
      )}
    </Stack>
  );
};

// ─── Platform Count Score Card ────────────────────────────────────────────────

const PlatformCountCard: React.FC<{ data: PlatformCountData; weight: number }> = ({ data, weight }) => {
  const t = THEMES.platform;
  const contribution = (data.score * weight) / 100;
  const growthColor = data.growth > 0 ? "#107C10" : data.growth < 0 ? "#D13438" : "#323130";
  const growthText = data.growth > 0 ? `+${data.growth}` : String(data.growth);

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{ root: { backgroundColor: t.bg, border: `1.5px solid ${t.border}`, borderRadius: 12, padding: "20px 24px", flex: "1 1 0", minWidth: 280 } }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: t.accent } }}>Platform Count Score</Text>
      <MetricHeader score={data.score} weight={weight} contribution={contribution} accent={t.accent} />

      <Stack tokens={{ childrenGap: 8 }}>
        <Text variant="smallPlus" styles={{ root: { fontWeight: 600, color: t.accent } }}>Factors</Text>
        {/* Platform device counts */}
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { flexWrap: "wrap" } }}>
          <FactorBox label="Android" value={fmtInt(data.android)} flex="1 1 0" />
          <FactorBox label="iOS" value={fmtInt(data.ios)} flex="1 1 0" />
          <FactorBox label="Mac" value={fmtInt(data.mac)} flex="1 1 0" />
          <FactorBox label="Windows" value={fmtInt(data.windows)} flex="1 1 0" />
        </Stack>
        {/* Current + Growth */}
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { flexWrap: "wrap" } }}>
          <FactorBox label="Current" value={`${data.current} platforms`} flex="1 1 0" minWidth={120} />
          <FactorBox label="Growth (vs 4-mo ago)" value={growthText} valueColor={growthColor} flex="1 1 0" minWidth={120} />
        </Stack>
      </Stack>

      {data.ruleText && (
        <Text variant="small" styles={{ root: { color: "#605E5C", fontStyle: "italic", textAlign: "center" } }}>
          Rule: {data.ruleText}
        </Text>
      )}

      {data.scoringRules && (
        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="small" styles={{ root: { fontWeight: 700, color: t.accent } }}>Scoring Rules:</Text>
          <Text variant="small" styles={{ root: { color: "#323130", lineHeight: "18px" } }}>{data.scoringRules}</Text>
        </Stack>
      )}
    </Stack>
  );
};

// ─── Management Scope Expansion Card ─────────────────────────────────────────

const MgmtScopeCard: React.FC<{ data: MgmtScopeData; weight: number }> = ({ data, weight }) => {
  const t = THEMES.mgmt;
  const contribution = (data.score * weight) / 100;
  const mdmColor = data.mdmChange > 0 ? "#107C10" : data.mdmChange < 0 ? "#D13438" : "#323130";
  const mamColor = data.mamChange > 0 ? "#107C10" : data.mamChange < 0 ? "#D13438" : "#323130";
  const suiteDate = data.suitePurchase ? formatISODate(data.suitePurchase) : "—";
  const suiteAge = data.suitePurchase ? getDateAge(data.suitePurchase) : "";
  const compositeDisplay = data.compositeEq.replace(/\+/g, " + ");

  const subRowStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{ root: { backgroundColor: t.bg, border: `1.5px solid ${t.border}`, borderRadius: 12, padding: "20px 24px", flex: "1 1 0", minWidth: 300 } }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: t.accent } }}>Mgmt Scope Expansion</Text>
      <MetricHeader score={data.score} weight={weight} contribution={contribution} accent={t.accent} />

      <Stack tokens={{ childrenGap: 8 }}>
        <Text variant="smallPlus" styles={{ root: { fontWeight: 600, color: t.accent } }}>Sub-Components</Text>

        {/* MDM row */}
        <div style={subRowStyle}>
          <Stack styles={{ root: { flex: 1 } }}>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>MDM Change (vs 4-mo avg)</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: mdmColor } }}>{fmtPct(data.mdmChange)}</Text>
          </Stack>
          <Stack horizontalAlign="end">
            <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>Score:</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: "#323130" } }}>{data.mdmScore}</Text>
          </Stack>
        </div>

        {/* MAM row */}
        <div style={subRowStyle}>
          <Stack styles={{ root: { flex: 1 } }}>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>MAM Change (vs 4-mo avg)</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: mamColor } }}>{fmtPct(data.mamChange)}</Text>
          </Stack>
          <Stack horizontalAlign="end">
            <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>Score:</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: "#323130" } }}>{data.mamScore}</Text>
          </Stack>
        </div>

        {/* Suite Purchase row */}
        <div style={subRowStyle}>
          <Stack styles={{ root: { flex: 1 } }}>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Suite Purchase</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: "#323130" } }}>
              {suiteDate}{suiteAge ? ` (${suiteAge})` : ""}
            </Text>
          </Stack>
          <Stack horizontalAlign="end">
            <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>Score:</Text>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: "#323130" } }}>{data.suiteScore}</Text>
          </Stack>
        </div>

        {/* Composite row */}
        <div style={{ ...subRowStyle, backgroundColor: t.bg, border: `1px solid ${t.border}` }}>
          <Text variant="medium" styles={{ root: { fontWeight: 700, color: t.accent } }}>Composite</Text>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="medium" styles={{ root: { color: "#605E5C", fontFamily: "Consolas, monospace" } }}>
              {compositeDisplay}
            </Text>
            <Text variant="medium" styles={{ root: { fontWeight: 700, color: t.accent } }}>
              = {data.compositeTotal}
            </Text>
          </Stack>
        </div>
      </Stack>

      {data.scoringBins && (
        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="small" styles={{ root: { fontWeight: 700, color: t.accent } }}>Scoring Bins:</Text>
          <Text variant="small" styles={{ root: { color: "#323130", lineHeight: "18px", whiteSpace: "pre-line" } }}>
            {data.scoringBins}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

// ─── main export ──────────────────────────────────────────────────────────────

const IntuneUsageTrajectoryRawIndicatorCards: React.FC<UsageTrajProps> = ({ rows }) => {
  const { usageData, platformData, mgmtData, weight, hasUsage, hasPlatform, hasMgmt } = useMemo(() => {
    const map = new Map<string, RawIndicatorRow[]>();
    for (const row of rows) {
      const section = row.sectionName ?? "Unknown";
      if (!map.has(section)) map.set(section, []);
      map.get(section)!.push(row);
    }

    const usageRows    = map.get("Usage Change Score") ?? [];
    const platformRows = map.get("Platform Count Score") ?? [];
    const mgmtRows     = map.get("Management Scope Expansion") ?? [];

    const activeCount = [usageRows, platformRows, mgmtRows].filter((r) => r.length > 0).length;
    const w = activeCount > 0 ? 100 / activeCount : 33.33;

    return {
      usageData:    parseUsageChange(usageRows),
      platformData: parsePlatformCount(platformRows),
      mgmtData:     parseMgmtScope(mgmtRows),
      weight:       w,
      hasUsage:     usageRows.length > 0,
      hasPlatform:  platformRows.length > 0,
      hasMgmt:      mgmtRows.length > 0,
    };
  }, [rows]);

  return (
    <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { flexWrap: "wrap", alignItems: "flex-start" } }}>
      {hasUsage    && <UsageChangeCard   data={usageData}    weight={weight} />}
      {hasPlatform && <PlatformCountCard data={platformData} weight={weight} />}
      {hasMgmt     && <MgmtScopeCard     data={mgmtData}     weight={weight} />}
    </Stack>
  );
};

export default IntuneUsageTrajectoryRawIndicatorCards;
